import { FC } from "react";

import { PageLink } from "./PageLink";

import { getPageType } from "components/materials/page/helpers";
import { displayOutcomeName } from "components/outcomes/helpers";
import { ActivityType, OutcomeAssessmentType, PageOutcomeType } from "components/server-types";
import { ScrollableTableContainer } from "components/tables/ScrollableTableContainer";
import { TD, TH, THead, TR, Table } from "components/tables/TableElements";
import { t } from "i18n/i18n";
import { Tooltip } from "mds/components/Tooltip";
import { ScoreIcon } from "mds/icons";
import { PageWithAssessmentsType } from "store/selectors";
import { NestedSectionOutcomeType } from "store/selectors/types";
import { averageAndRound } from "utils/math";

type StudentOutcomeScoreTableProps = {
  pagesWithAssessments: PageWithAssessmentsType[];
  activitiesById: Record<string, ActivityType>;
  sectionOutcomes: NestedSectionOutcomeType[];
  pageOutcomes: PageOutcomeType[];
  allOutcomeAssessments: OutcomeAssessmentType[];
};

export const StudentOutcomeScoreTable: FC<StudentOutcomeScoreTableProps> = ({
  pagesWithAssessments,
  activitiesById,
  sectionOutcomes,
  pageOutcomes,
  allOutcomeAssessments,
}) => {
  if (pagesWithAssessments.length === 0) {
    return null;
  }

  return (
    <ScrollableTableContainer title={t("assessment_dashboard.student_outcome_index_title")}>
      <Table>
        {/* Headers: Page / Page Type/ Outcome A / Outcome B / Outcome C */}
        {/* One row per student, showing each student's aggregate outcome scores */}
        <THead>
          <TR>
            <TH className="w-4/12 pl-4">{t("assessment_dashboard.all.page_header_title")}</TH>

            <TH className="w-2/12">
              <Tooltip text={t("assessment_dashboard.all.assessment_category_header_tooltip")}>
                {t("assessment_dashboard.all.assessment_category_header_title")}
              </Tooltip>
            </TH>

            {sectionOutcomes.map((sectionOutcome) => (
              <TH
                key={sectionOutcome.id}
                verticalDivClassName="max-h-[180px]"
                verticalDivTitle={displayOutcomeName(sectionOutcome.outcome)}
                isVertical
              >
                {displayOutcomeName(sectionOutcome.outcome)}
              </TH>
            ))}
          </TR>
        </THead>

        <tbody>
          {pagesWithAssessments.map((page) => (
            <TR key={page.id}>
              <TD className="min-w-0 max-w-24 truncate py-2.5 pl-[17px]">
                <PageLink className="truncate text-blue-shade-20 underline" page={page} />
              </TD>

              <TD>
                {getPageType(
                  page.category,
                  page.activity_id ? activitiesById[page.activity_id]?.category : undefined,
                )}
              </TD>

              {sectionOutcomes.map((sectionOutcome) => {
                const userOutcomeAssessmentsForThisOutcome = allOutcomeAssessments.filter((oa) =>
                  pageOutcomes.some(
                    (po) =>
                      po.page_id === page.id &&
                      po.section_outcome_id === sectionOutcome.id &&
                      oa.page_outcome_id === po.id,
                  ),
                );

                const filteredUserOutcomeAssessmentsForThisOutcome =
                  userOutcomeAssessmentsForThisOutcome.filter(
                    (oa) => oa.score !== null && oa.score !== undefined,
                  );
                const outcomeScore = filteredUserOutcomeAssessmentsForThisOutcome.length
                  ? averageAndRound(
                      filteredUserOutcomeAssessmentsForThisOutcome.map((oa) => oa.score),
                    )
                  : NaN;

                return (
                  <TD key={sectionOutcome.id}>
                    <ScoreIcon value={outcomeScore} />
                  </TD>
                );
              })}
            </TR>
          ))}
        </tbody>
      </Table>
    </ScrollableTableContainer>
  );
};
